import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MonitorSmartphone, ShieldAlert, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, LOGO } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export function getDeviceId() {
  let id = localStorage.getItem("pos_device_id");
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) ||
      `dev-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem("pos_device_id", id);
  }
  return id;
}

export default function StationGate({ children }) {
  const { user, logout } = useAuth();
  const rid = user?.restaurant_id;
  const cached = localStorage.getItem("pos_station_activated") === rid;
  const [ok, setOk] = useState(cached);
  const [checked, setChecked] = useState(cached);
  const [state, setState] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/stations/status", { params: { device_id: getDeviceId() } });
      setState(data);
      if (data.activated) {
        localStorage.setItem("pos_station_activated", rid);
        setOk(true);
      } else {
        localStorage.removeItem("pos_station_activated");
        setOk(false);
      }
    } catch (e) {
      // Only fall through on a network error (no HTTP response); block on 4xx/5xx
      if (!e?.response) {
        setOk(true);
      } else {
        localStorage.removeItem("pos_station_activated");
        setOk(false);
      }
    } finally {
      setChecked(true);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activate = async () => {
    if (state?.requires_password && !password) { toast.error("Ingresa la contraseña de activación"); return; }
    setBusy(true);
    try {
      await api.post("/stations/activate", { device_id: getDeviceId(), name, password });
      toast.success("Dispositivo activado como estación");
      localStorage.setItem("pos_station_activated", rid);
      setOk(true);
    } catch (e) {
      toast.error(apiError(e));
      load();
    } finally {
      setBusy(false);
    }
  };

  if (ok || user?.home_restaurant_id) return children;
  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="animate-spin text-[#1F2937]" size={36} />
      </div>
    );
  }

  const noQuota = state && state.available <= 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F172A] p-6" data-testid="station-gate">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white rounded-2xl p-8 shadow-2xl"
      >
        <div className="flex justify-center mb-6">
          <img src={LOGO} alt="logo" className="h-12" />
        </div>

        <div className="flex items-center justify-center mb-4">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${noQuota ? "bg-[#DC2626]/10" : "bg-[#E85D04]/10"}`}>
            {noQuota
              ? <ShieldAlert size={30} className="text-[#DC2626]" />
              : <MonitorSmartphone size={30} className="text-[#E85D04]" />}
          </div>
        </div>

        <h1 className="font-heading text-2xl font-extrabold text-center text-[#111827]">
          {noQuota ? "Sin estaciones disponibles" : "Activar este dispositivo"}
        </h1>
        <p className="text-sm text-[#111827]/60 text-center mt-2">
          {noQuota
            ? "Alcanzaste el máximo de estaciones activas de tu licencia. Adquiere más estaciones desde el panel para usar el POS en este dispositivo."
            : "Este dispositivo aún no está autorizado. Actívalo como una estación para empezar a usar el sistema."}
        </p>

        {state && (
          <div className="mt-5 flex items-center justify-center gap-6 bg-[#F8FAFC] rounded-xl py-3">
            <div className="text-center">
              <p className="font-heading text-2xl font-extrabold text-[#1F2937] tabular-nums" data-testid="gate-used">{state.used}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#111827]/50">En uso</p>
            </div>
            <div className="w-px h-8 bg-[#111827]/10" />
            <div className="text-center">
              <p className="font-heading text-2xl font-extrabold text-[#16A34A] tabular-nums" data-testid="gate-available">{state.available}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#111827]/50">Disponibles</p>
            </div>
            <div className="w-px h-8 bg-[#111827]/10" />
            <div className="text-center">
              <p className="font-heading text-2xl font-extrabold text-[#111827]/70 tabular-nums" data-testid="gate-max">{state.max}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#111827]/50">Máximo</p>
            </div>
          </div>
        )}

        {!noQuota && (
          <div className="mt-5">
            <label className="text-xs font-bold uppercase tracking-wider text-[#111827]/50">Nombre del dispositivo (opcional)</label>
            <input
              data-testid="station-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Caja principal, Tablet mesero 1"
              className="w-full mt-1 px-4 h-12 rounded-xl border border-[#111827]/15 bg-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#E85D04]"
            />
            {state?.requires_password && (
              <>
                <label className="block mt-4 text-xs font-bold uppercase tracking-wider text-[#111827]/50">Contraseña de activación</label>
                <input
                  data-testid="station-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && activate()}
                  placeholder="La define el administrador"
                  className="w-full mt-1 px-4 h-12 rounded-xl border border-[#111827]/15 bg-[#F8FAFC] font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-[#E85D04]"
                />
              </>
            )}
            <button
              data-testid="activate-station-btn"
              disabled={busy}
              onClick={activate}
              className="w-full mt-4 h-12 bg-[#E85D04] text-white rounded-xl font-semibold hover:bg-[#C2410C] disabled:opacity-60 transition-colors active:scale-[0.98]"
            >
              {busy ? "Activando…" : "Activar este dispositivo"}
            </button>
          </div>
        )}

        <button
          data-testid="gate-logout-btn"
          onClick={logout}
          className="w-full mt-3 h-11 flex items-center justify-center gap-2 text-sm text-[#111827]/60 hover:text-[#DC2626] transition-colors"
        >
          <LogOut size={16} /> Cerrar sesión
        </button>
      </motion.div>
    </div>
  );
}
